import {
  filterToRenderableDocuments,
  getDocumentReferencesById,
} from '@ctw/shared/api/fhir/document';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { searchProvenancesFQS } from '@ctw/shared/api/fqs/queries/provenances';
import { isRenderableBinary } from '@ctw/shared/content/resource/filters';
import type { CTWState } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import {
  QUERY_KEY_BINARY,
  QUERY_KEY_BINARY_ID,
  QUERY_KEY_CCDA_PATIENT,
  QUERY_KEY_DOCUMENT_REFERENCE_FOR_BINARY_ID,
} from '@ctw/shared/utils/query-keys';
import type { Binary, Provenance, Resource } from 'fhir/r4';
import { compact, find, some } from 'lodash-es';
import type { FHIRModel } from './models/fhir-model';
import { getBuilderFhirPatientByIdentifier } from './patient-helper';
import { searchProvenances } from './provenance';
import { SYSTEM_ZUS_UNIVERSAL_ID } from './system-urls';

// Get the binary ID from provenance, if document is renderable
export function getBinaryId(provenances: Array<Provenance>, targetId: string): string | undefined {
  for (const provenance of provenances) {
    const hasTarget = some(provenance.target, (t) => t.reference?.includes(targetId));

    if (hasTarget && isRenderableBinary(provenance)) {
      const source = find(provenance.entity, { role: 'source' });
      if (source?.what.reference) {
        // Return the ID portion of the reference.
        return source.what.reference.split('/')[1];
      }
    }
  }

  return undefined;
}

// We optionally look for any associated binary CCDAs
// if getSourceDocument is true.
export function useBinaryId<T extends Resource, M extends FHIRModel<T>>(
  model: M | undefined,
  getSourceDocument?: boolean,
  shouldFetchBinaryDocument?: boolean,
) {
  return useCtwQuery({
    queryId: QUERY_KEY_BINARY_ID,
    queryKey: [model?.id, getSourceDocument],
    queryFn: async ({ graphqlClient }) => {
      if (model instanceof DocumentModel) {
        return model.binaryId;
      }

      if (!model) {
        return null;
      }

      if (model instanceof DocumentModel) {
        // Special handling for document models which already have a binaryID.
        return model.binaryId ?? null;
      }
      if (getSourceDocument && !model.isSummaryResource) {
        const provenances = await searchProvenances(graphqlClient, [model]);
        return getBinaryId(provenances, model.id) ?? null;
      }

      return null;
    },
    enabled: shouldFetchBinaryDocument,
  });
}

export function useDocumentReferenceForBinaryId(upid: string, binaryId: string, enabled: boolean) {
  return useCtwQuery({
    queryId: QUERY_KEY_DOCUMENT_REFERENCE_FOR_BINARY_ID,
    queryKey: [upid, binaryId],
    queryFn: async ({ telemetry, graphqlClient }) => {
      const provenance = (await searchProvenancesFQS(graphqlClient, 'Binary', [binaryId]))?.[0];

      if (!provenance) {
        telemetry.trackError({
          message: 'No provenance found for binary',
          error: new Error('Unable to find DocumentReference for binary'),
          context: {
            binaryId,
          },
        });
        return null;
      }

      const documentReferenceIds = compact(
        provenance.target.map((target) =>
          target.reference?.includes('DocumentReference') ? target.reference?.split('/')[1] : null,
        ),
      );

      const documentReferences = await getDocumentReferencesById(
        telemetry,
        graphqlClient,
        upid,
        documentReferenceIds,
      );

      return filterToRenderableDocuments(documentReferences).find(
        (documentReference) => documentReference.binaryId === binaryId,
      );
    },
    enabled,
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.readAsDataURL(blob);

    reader.onload = () => {
      const base64String = reader.result?.toString().split(',')[1] ?? ''; // Remove data: prefix
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const text = new TextDecoder().decode(bytes);
      resolve(text);
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
}

export type BinaryData = {
  binary: Binary & {
    blob: Blob;
  };
};

export type FQSBinaryJSON = {
  data: {
    contentType: string;
    id: string;
    meta: Binary['meta'];
    resourceType: 'Binary';
    securityContext: {
      reference: string;
      type: string;
    };
  };
};

export async function getBinaryDocument(
  fetchFromFqs: CTWState['fetchFromFqs'],
  binaryId: string,
): Promise<BinaryData> {
  const { data, headers } = await fetchFromFqs(`Binary/${binaryId}`, {
    method: 'GET',
  });

  if (data instanceof Blob) {
    // The Smile response is a base64 string but FQS returns a blob.
    // The blob can easily convert to base64 string, but is flaky converting
    // back from base64 blob because we strip off any header information the
    // blob had when converting to base64 decoded string. So we add `blob` to
    // the BinaryData object so that we have it later as-is.

    return {
      binary: {
        blob: data,
        data: await blobToBase64(data),
        contentType: headers.get('Content-Type') || 'unknown',
        resourceType: 'Binary',
      },
    };
  }
  throw new Error('Response from FQS was not a Blob');
}

export const useGetBinaryDocument = (binaryId: string, enabled = true) =>
  useCtwQuery({
    queryId: QUERY_KEY_BINARY,
    queryKey: [binaryId],
    queryFn: async ({ fetchFromFqs }) => getBinaryDocument(fetchFromFqs, binaryId),
    enabled,
  });

export const useGetPatientFromBinaryId = (binaryId: string, enabled: boolean) =>
  useCtwQuery({
    queryId: QUERY_KEY_CCDA_PATIENT,
    queryKey: [binaryId],
    queryFn: async ({
      ctwFetch,
      requestContext,
      fetchFromFqs,
    }): Promise<PatientModel | undefined> => {
      const binaryToUpidRequest = (await fetchFromFqs(`rest/Binary/${binaryId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/fhir+json',
        },
      })) as FQSBinaryJSON;

      const upid = find(binaryToUpidRequest.data.meta?.extension, {
        url: SYSTEM_ZUS_UNIVERSAL_ID,
      })?.valueString;

      if (upid) {
        return getBuilderFhirPatientByIdentifier(
          ctwFetch,
          requestContext,
          upid,
          SYSTEM_ZUS_UNIVERSAL_ID,
        );
      }

      return undefined;
    },
    enabled,
  });
